/**
 * Client-side component patterns for quick actions
 *
 * These mirror the server-side patterns but are optimized for client bundling.
 * When modifying patterns here, also update the server-side patterns in
 * packages/builder-types/src/component-patterns.ts
 */

/** Pattern UI definition structure */
export interface PatternUIDefinition {
  root: Record<string, unknown>;
  actions?: Record<string, unknown>;
  input?: Record<string, unknown>;
}

/** Component pattern with metadata */
export interface ComponentPattern {
  name: string;
  description: string;
  ui: PatternUIDefinition;
}

/**
 * Available component patterns for quick actions
 * Note: Keep in sync with server-side COMPONENT_PATTERNS
 */
export const COMPONENT_PATTERNS: Record<string, ComponentPattern> = {
  'visibility-toggle': {
    name: 'Visibility Toggle',
    description: 'Button that shows/hides content',
    ui: {
      root: {
        type: 'container',
        props: { direction: 'column', gap: 16, padding: 16 },
        children: [
          {
            type: 'button',
            id: 'toggle-btn',
            props: { label: 'Toggle Content', variant: 'primary' },
            events: { onClick: { actions: ['toggle-visibility'] } },
          },
          {
            type: 'container',
            id: 'content-wrapper',
            style: {
              opacity: '{{input.visible ? 1 : 0}}',
              transition: 'opacity 0.2s ease-in-out',
              minHeight: '48px',
            },
            children: [
              {
                type: 'card',
                props: { padding: 16 },
                children: [
                  {
                    type: 'text',
                    props: { content: 'This content can be toggled.', variant: 'body' },
                  },
                ],
              },
            ],
          },
        ],
      },
      actions: {
        'toggle-visibility': {
          type: 'updateContext',
          updates: { visible: '{{!input.visible}}' },
        },
      },
      input: { visible: false },
    },
  },

  'timed-message': {
    name: 'Timed Message',
    description: 'Shows then auto-hides',
    ui: {
      root: {
        type: 'container',
        props: { direction: 'column', gap: 16, padding: 16 },
        children: [
          {
            type: 'button',
            id: 'show-btn',
            props: { label: 'Show Message', variant: 'primary' },
            events: { onClick: { actions: ['show-message', 'hide-after-delay'] } },
          },
          {
            type: 'container',
            id: 'message-container',
            style: {
              opacity: '{{input.showMessage ? 1 : 0}}',
              transition: 'opacity 0.3s ease-in-out',
              minHeight: '48px',
            },
            children: [
              {
                type: 'card',
                props: { padding: 12 },
                style: { backgroundColor: '#e8f5e9', borderRadius: '8px' },
                children: [
                  {
                    type: 'text',
                    props: {
                      content: 'This message will disappear in 3 seconds!',
                      variant: 'body',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      actions: {
        'show-message': { type: 'updateContext', updates: { showMessage: true } },
        'hide-after-delay': {
          type: 'updateContext',
          updates: { showMessage: false },
          delayMs: 3000,
        },
      },
      input: { showMessage: false },
    },
  },

  'simple-form': {
    name: 'Contact Form',
    description: 'Form with inputs and submit',
    ui: {
      root: {
        type: 'container',
        props: { direction: 'column', gap: 16, padding: 16 },
        style: { maxWidth: '400px' },
        children: [
          { type: 'text', props: { content: 'Contact Form', variant: 'h2' } },
          {
            type: 'form',
            id: 'contact-form',
            children: [
              {
                type: 'container',
                props: { direction: 'column', gap: 12 },
                children: [
                  {
                    type: 'input',
                    id: 'name-input',
                    props: { label: 'Name', name: 'name', placeholder: 'Enter your name' },
                  },
                  {
                    type: 'input',
                    id: 'email-input',
                    props: {
                      label: 'Email',
                      name: 'email',
                      type: 'email',
                      placeholder: 'Enter your email',
                    },
                  },
                  {
                    type: 'textarea',
                    id: 'message-input',
                    props: {
                      label: 'Message',
                      name: 'message',
                      placeholder: 'Enter your message',
                      rows: 4,
                    },
                  },
                  {
                    type: 'button',
                    id: 'submit-btn',
                    props: { label: 'Submit', variant: 'primary' },
                    events: { onClick: { actions: ['submit-form'] } },
                  },
                ],
              },
            ],
          },
          {
            type: 'container',
            id: 'success-message',
            style: { opacity: '{{input.submitted ? 1 : 0}}', transition: 'opacity 0.3s' },
            children: [
              {
                type: 'text',
                props: { content: 'Thank you! Your message has been submitted.', variant: 'body' },
                style: { color: '#2e7d32' },
              },
            ],
          },
        ],
      },
      actions: { 'submit-form': { type: 'updateContext', updates: { submitted: true } } },
      input: { submitted: false },
    },
  },

  'card-grid': {
    name: 'Card Grid',
    description: 'Responsive card layout',
    ui: {
      root: {
        type: 'container',
        props: { direction: 'column', gap: 16, padding: 16 },
        children: [
          { type: 'text', props: { content: 'Featured Items', variant: 'h2' } },
          {
            type: 'container',
            props: { direction: 'row', gap: 16 },
            style: { flexWrap: 'wrap' },
            children: [
              {
                type: 'card',
                id: 'card-1',
                props: { title: 'Item One', padding: 16 },
                style: { minWidth: '200px', flex: '1' },
                children: [
                  {
                    type: 'text',
                    props: { content: 'Description for the first item.', variant: 'body' },
                  },
                  {
                    type: 'button',
                    props: { label: 'Learn More', variant: 'outline' },
                    style: { marginTop: '12px' },
                  },
                ],
              },
              {
                type: 'card',
                id: 'card-2',
                props: { title: 'Item Two', padding: 16 },
                style: { minWidth: '200px', flex: '1' },
                children: [
                  {
                    type: 'text',
                    props: { content: 'Description for the second item.', variant: 'body' },
                  },
                  {
                    type: 'button',
                    props: { label: 'Learn More', variant: 'outline' },
                    style: { marginTop: '12px' },
                  },
                ],
              },
              {
                type: 'card',
                id: 'card-3',
                props: { title: 'Item Three', padding: 16 },
                style: { minWidth: '200px', flex: '1' },
                children: [
                  {
                    type: 'text',
                    props: { content: 'Description for the third item.', variant: 'body' },
                  },
                  {
                    type: 'button',
                    props: { label: 'Learn More', variant: 'outline' },
                    style: { marginTop: '12px' },
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },

  counter: {
    name: 'Counter',
    description: '+/- buttons with number',
    ui: {
      root: {
        type: 'container',
        props: { direction: 'column', gap: 16, padding: 16 },
        style: { alignItems: 'center' },
        children: [
          { type: 'text', props: { content: 'Counter', variant: 'h2' } },
          {
            type: 'container',
            props: { direction: 'row', gap: 16 },
            style: { alignItems: 'center' },
            children: [
              {
                type: 'button',
                id: 'decrement-btn',
                props: { label: '-', variant: 'outline' },
                style: { width: '48px', height: '48px' },
                events: { onClick: { actions: ['decrement'] } },
              },
              {
                type: 'text',
                id: 'count-display',
                props: { content: '{{input.count}}', variant: 'h1' },
                style: { minWidth: '60px', textAlign: 'center' },
              },
              {
                type: 'button',
                id: 'increment-btn',
                props: { label: '+', variant: 'outline' },
                style: { width: '48px', height: '48px' },
                events: { onClick: { actions: ['increment'] } },
              },
            ],
          },
          {
            type: 'button',
            id: 'reset-btn',
            props: { label: 'Reset', variant: 'secondary' },
            events: { onClick: { actions: ['reset'] } },
          },
        ],
      },
      actions: {
        increment: { type: 'updateContext', updates: { count: '{{input.count + 1}}' } },
        decrement: { type: 'updateContext', updates: { count: '{{input.count - 1}}' } },
        reset: { type: 'updateContext', updates: { count: 0 } },
      },
      input: { count: 0 },
    },
  },

  accordion: {
    name: 'Accordion',
    description: 'Collapsible sections',
    ui: {
      root: {
        type: 'container',
        props: { direction: 'column', gap: 8, padding: 16 },
        children: [
          {
            type: 'text',
            props: { content: 'Frequently Asked Questions', variant: 'h2' },
            style: { marginBottom: '16px' },
          },
          {
            type: 'container',
            id: 'section-1',
            props: { direction: 'column' },
            style: { border: '1px solid #e0e0e0', borderRadius: '8px' },
            children: [
              {
                type: 'button',
                id: 'section-1-header',
                props: { label: 'What is this?', variant: 'outline' },
                style: { width: '100%', justifyContent: 'flex-start' },
                events: { onClick: { actions: ['toggle-section-1'] } },
              },
              {
                type: 'container',
                id: 'section-1-content',
                style: {
                  opacity: '{{input.section1Open ? 1 : 0}}',
                  maxHeight: '{{input.section1Open ? "200px" : "0px"}}',
                  overflow: 'hidden',
                  transition: 'opacity 0.2s, max-height 0.3s',
                  padding: '{{input.section1Open ? "16px" : "0 16px"}}',
                },
                children: [
                  {
                    type: 'text',
                    props: {
                      content: 'This is an accordion component that shows/hides content.',
                      variant: 'body',
                    },
                  },
                ],
              },
            ],
          },
          {
            type: 'container',
            id: 'section-2',
            props: { direction: 'column' },
            style: { border: '1px solid #e0e0e0', borderRadius: '8px' },
            children: [
              {
                type: 'button',
                id: 'section-2-header',
                props: { label: 'How does it work?', variant: 'outline' },
                style: { width: '100%', justifyContent: 'flex-start' },
                events: { onClick: { actions: ['toggle-section-2'] } },
              },
              {
                type: 'container',
                id: 'section-2-content',
                style: {
                  opacity: '{{input.section2Open ? 1 : 0}}',
                  maxHeight: '{{input.section2Open ? "200px" : "0px"}}',
                  overflow: 'hidden',
                  transition: 'opacity 0.2s, max-height 0.3s',
                  padding: '{{input.section2Open ? "16px" : "0 16px"}}',
                },
                children: [
                  {
                    type: 'text',
                    props: {
                      content: 'Click headers to expand/collapse each section.',
                      variant: 'body',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      actions: {
        'toggle-section-1': {
          type: 'updateContext',
          updates: { section1Open: '{{!input.section1Open}}' },
        },
        'toggle-section-2': {
          type: 'updateContext',
          updates: { section2Open: '{{!input.section2Open}}' },
        },
      },
      input: { section1Open: false, section2Open: false },
    },
  },

  tabs: {
    name: 'Tabs',
    description: 'Tabbed content panels',
    ui: {
      root: {
        type: 'container',
        props: { direction: 'column', gap: 0, padding: 16 },
        children: [
          {
            type: 'container',
            props: { direction: 'row', gap: 0 },
            style: { borderBottom: '1px solid #e0e0e0' },
            children: [
              {
                type: 'button',
                id: 'tab-1',
                props: { label: 'Tab 1', variant: 'outline' },
                style: {
                  borderRadius: '8px 8px 0 0',
                  borderBottom: '{{input.activeTab === 1 ? "2px solid #1976d2" : "none"}}',
                  backgroundColor: '{{input.activeTab === 1 ? "#e3f2fd" : "transparent"}}',
                },
                events: { onClick: { actions: ['select-tab-1'] } },
              },
              {
                type: 'button',
                id: 'tab-2',
                props: { label: 'Tab 2', variant: 'outline' },
                style: {
                  borderRadius: '8px 8px 0 0',
                  borderBottom: '{{input.activeTab === 2 ? "2px solid #1976d2" : "none"}}',
                  backgroundColor: '{{input.activeTab === 2 ? "#e3f2fd" : "transparent"}}',
                },
                events: { onClick: { actions: ['select-tab-2'] } },
              },
              {
                type: 'button',
                id: 'tab-3',
                props: { label: 'Tab 3', variant: 'outline' },
                style: {
                  borderRadius: '8px 8px 0 0',
                  borderBottom: '{{input.activeTab === 3 ? "2px solid #1976d2" : "none"}}',
                  backgroundColor: '{{input.activeTab === 3 ? "#e3f2fd" : "transparent"}}',
                },
                events: { onClick: { actions: ['select-tab-3'] } },
              },
            ],
          },
          {
            type: 'container',
            props: { padding: 16 },
            style: { border: '1px solid #e0e0e0', borderTop: 'none', minHeight: '100px' },
            children: [
              {
                type: 'text',
                visible: '{{input.activeTab === 1}}',
                props: { content: 'Content for Tab 1.', variant: 'body' },
              },
              {
                type: 'text',
                visible: '{{input.activeTab === 2}}',
                props: { content: 'Content for Tab 2.', variant: 'body' },
              },
              {
                type: 'text',
                visible: '{{input.activeTab === 3}}',
                props: { content: 'Content for Tab 3.', variant: 'body' },
              },
            ],
          },
        ],
      },
      actions: {
        'select-tab-1': { type: 'updateContext', updates: { activeTab: 1 } },
        'select-tab-2': { type: 'updateContext', updates: { activeTab: 2 } },
        'select-tab-3': { type: 'updateContext', updates: { activeTab: 3 } },
      },
      input: { activeTab: 1 },
    },
  },

  'multi-step-wizard': {
    name: 'Multi-Step Wizard',
    description: 'Step-by-step workflow with AI, files, and download',
    ui: {
      root: {
        type: 'container',
        props: { direction: 'column', gap: 0 },
        children: [
          {
            type: 'stepper',
            id: 'progress-stepper',
            props: {
              steps: ['Input', 'AI Processing', 'Review & Download'],
              currentStep: '{{input.currentStep}}',
            },
          },
          {
            type: 'container',
            id: 'page-input',
            visible: '{{input.currentStep === 0}}',
            props: { direction: 'column', gap: 16, padding: 24 },
            children: [
              { type: 'text', props: { content: 'Step 1: Enter Your Points', variant: 'h2' } },
              {
                type: 'bulletListInput',
                id: 'bullet-input',
                props: {
                  name: 'bullets',
                  label: 'Key Points',
                  placeholder: 'Type a point and press Enter...',
                  maxItems: 20,
                },
              },
              {
                type: 'button',
                id: 'next-to-process',
                props: { label: 'Process with AI', variant: 'primary' },
                events: { onClick: { actions: ['go-to-processing'] } },
              },
            ],
          },
          {
            type: 'container',
            id: 'page-processing',
            visible: '{{input.currentStep === 1}}',
            props: { direction: 'column', gap: 16, padding: 24 },
            children: [
              { type: 'text', props: { content: 'Step 2: AI Processing', variant: 'h2' } },
              {
                type: 'markdown',
                id: 'ai-result-display',
                props: {
                  content: '{{output.aiResult || "Click Generate to start..."}}',
                  maxHeight: '400px',
                },
              },
              {
                type: 'container',
                props: { direction: 'row', gap: 12 },
                children: [
                  {
                    type: 'button',
                    props: { label: 'Back', variant: 'outline' },
                    events: { onClick: { actions: ['go-to-input'] } },
                  },
                  {
                    type: 'button',
                    id: 'generate-btn',
                    props: { label: 'Generate', variant: 'primary' },
                    events: { onClick: { actions: ['invoke-ai'] } },
                  },
                  {
                    type: 'button',
                    props: { label: 'Next', variant: 'primary' },
                    events: { onClick: { actions: ['go-to-review'] } },
                  },
                ],
              },
            ],
          },
          {
            type: 'container',
            id: 'page-review',
            visible: '{{input.currentStep === 2}}',
            props: { direction: 'column', gap: 16, padding: 24 },
            children: [
              { type: 'text', props: { content: 'Step 3: Review & Download', variant: 'h2' } },
              {
                type: 'markdown',
                id: 'final-result',
                props: {
                  content: '{{output.aiResult || "No result yet."}}',
                  maxHeight: '400px',
                },
              },
              {
                type: 'container',
                props: { direction: 'row', gap: 12 },
                children: [
                  {
                    type: 'button',
                    props: { label: 'Back', variant: 'outline' },
                    events: { onClick: { actions: ['go-to-processing'] } },
                  },
                  {
                    type: 'button',
                    id: 'download-btn',
                    props: { label: 'Download', variant: 'primary' },
                    events: { onClick: { actions: ['download-file'] } },
                  },
                ],
              },
            ],
          },
        ],
      },
      actions: {
        'go-to-input': { type: 'updateContext', updates: { currentStep: 0 } },
        'go-to-processing': { type: 'updateContext', updates: { currentStep: 1 } },
        'go-to-review': { type: 'updateContext', updates: { currentStep: 2 } },
      },
      input: { currentStep: 0, bullets: [] },
    },
  },

  'hero-section': {
    name: 'Hero Section',
    description: 'Landing page header',
    ui: {
      root: {
        type: 'container',
        props: { direction: 'column', gap: 24, padding: 48 },
        style: { textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: '12px' },
        children: [
          { type: 'text', props: { content: 'Welcome to Our Platform', variant: 'h1' } },
          {
            type: 'text',
            props: {
              content:
                'Build amazing things with our powerful tools. Get started today and transform your ideas into reality.',
              variant: 'body',
            },
            style: { maxWidth: '600px', margin: '0 auto', color: '#666' },
          },
          {
            type: 'container',
            props: { direction: 'row', gap: 16 },
            style: { justifyContent: 'center' },
            children: [
              {
                type: 'button',
                id: 'primary-cta',
                props: { label: 'Get Started', variant: 'primary' },
              },
              {
                type: 'button',
                id: 'secondary-cta',
                props: { label: 'Learn More', variant: 'outline' },
              },
            ],
          },
        ],
      },
    },
  },
};
